/**
 * QR code as a list of horizontal dark runs, ready to draw with plain views.
 *
 * `qrcode`'s renderers can't run in React Native: Metro picks the package's
 * `browser` build, whose `toDataURL` needs `document` to make a <canvas> and
 * throws "You need to specify a canvas element" without one. `QRCode.create`
 * only computes the module matrix, so it works on iOS, Android and web alike.
 */
import QRCode from 'qrcode';

/** A dark run: `len` modules starting at (`row`, `col`). */
export type QrRun = readonly [row: number, col: number, len: number];

export interface QrRuns {
  /** Modules per side, excluding the quiet zone. */
  size: number;
  runs: QrRun[];
}

export function qrRuns(
  value: string,
  errorCorrectionLevel: QRCode.QRCodeErrorCorrectionLevel = 'M',
): QrRuns {
  const { modules } = QRCode.create(value, { errorCorrectionLevel });
  const n = modules.size;
  const runs: QrRun[] = [];
  for (let row = 0; row < n; row++) {
    let col = 0;
    while (col < n) {
      if (!modules.get(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < n && modules.get(row, col)) col++;
      runs.push([row, start, col - start]);
    }
  }
  return { size: n, runs };
}
